import { atom } from 'jotai';
import { atomFamily, atomWithDefault, loadable } from 'jotai/utils';
import {
  IKPIRequestDataModel,
  IKPIRequestResponse,
  KPIRequestStatus,
} from '../../../data-models/kpi-requests.data-model';
import {
  fetchKPIRequestById,
  fetchKPIRequestResponseById,
  fetchKPIRequests,
} from '../../queries/KPIRequestsQueries';

export const kpisAssignRequestModalState = atom<number>(-1);

export const selectedRequestState = atom<IKPIRequestDataModel | null>(null);

export enum KpiAction {
  archive = 'archive',
  sendReminder = 'sendReminder',
  resend = 'resend',
}

export const showModalStateKPI = atom<KpiAction | null>(null);

const ALLOWED_KPI_REQUEST_STATUS = new Set<string>([
  KPIRequestStatus.ManagedServiceSubmitted,
  KPIRequestStatus.ManagedServiceInProgress,
  KPIRequestStatus.ManagedServiceFinished,
  KPIRequestStatus.Submitted,
  KPIRequestStatus.Accepted,
  KPIRequestStatus.Rejected,
  KPIRequestStatus.Sent,
]);

/**
 * UI: error modal for not-sent KPI requests. Pre-migrated in PR 5.
 */
export const kpiRequestErrorModalOpenState = atom<boolean>(false);

/**
 * UI: list of request ids that failed to send. Pre-migrated in PR 5.
 */
export const kpiRequestsNotSentState = atom<number[]>([]);

/**
 * Fetches all KPI requests on first subscribe. Consumers using
 * `useAtomValue` will suspend; use {@link kpisRequestsListStateLoadableJ}
 * for non-suspending callers.
 */
export const kpisRequestsListStateJ = atomWithDefault<
  IKPIRequestDataModel[] | null | Promise<IKPIRequestDataModel[] | null>
>(() => fetchKPIRequests());

export const kpisRequestsListStateLoadableJ = loadable(kpisRequestsListStateJ);

/**
 * Per-company KPI requests (accepted + rejected, merged).
 *
 * Sentinel guard: callers may pass `selectedCompanyIdProfileAtom`'s
 * default value `-1`. The guard returns an empty array to avoid issuing
 * a fetch with `companyId=-1`.
 */
export const kpiRequestsByCompanyIdStateJ = atomFamily((companyId: number) =>
  atomWithDefault<IKPIRequestDataModel[] | Promise<IKPIRequestDataModel[]>>(async () => {
    if (companyId <= 0) {
      return [];
    }
    const [accepted, rejected] = await Promise.all([
      fetchKPIRequests({ companyId, status: KPIRequestStatus.Accepted }),
      fetchKPIRequests({ companyId, status: KPIRequestStatus.Rejected }),
    ]);
    return [...(accepted ?? []), ...(rejected ?? [])];
  })
);

/**
 * Counts of KPI requests grouped by status. Derived from
 * {@link kpisRequestsListStateJ}.
 */
export const kpiRequestsCountStateJ = atom(async (get) => {
  const requests = await get(kpisRequestsListStateJ);
  const counts: Record<KPIRequestStatus, number> = {
    [KPIRequestStatus.Submitted]: 0,
    [KPIRequestStatus.Accepted]: 0,
    [KPIRequestStatus.Rejected]: 0,
    [KPIRequestStatus.NotSent]: 0,
    [KPIRequestStatus.Sent]: 0,
    [KPIRequestStatus.Archived]: 0,
    [KPIRequestStatus.ManagedServiceSubmitted]: 0,
    [KPIRequestStatus.ManagedServiceInProgress]: 0,
    [KPIRequestStatus.ManagedServiceFinished]: 0,
  };

  requests?.forEach((request) => {
    if (!request.status) {
      return;
    }
    counts[request.status as KPIRequestStatus] += 1;
  });
  return counts;
});

/**
 * Map of request id → request, derived from
 * {@link kpisRequestsListStateJ}.
 */
export const kpisRequestsMapByIdStateJ = atom(async (get) => {
  const list = await get(kpisRequestsListStateJ);
  const map = new Map<number, IKPIRequestDataModel>();
  if (!list) {
    return map;
  }
  list.forEach((request) => map.set(request.id, request));
  return map;
});

export const kpisRequestsMapByIdStateLoadableJ = loadable(kpisRequestsMapByIdStateJ);

/**
 * Requests filtered by status set. `atomFamily` needs a stable primitive key,
 * so the family is keyed by `statuses.join('|')`. Callers should use the
 * {@link filterKPIRequestsByStatusesJ} helper, which accepts the array.
 */
const filterKPIRequestsByStatusesFamily = atomFamily((statusesKey: string) =>
  atom(async (get) => {
    const list = await get(kpisRequestsListStateJ);
    if (!list) {
      return [];
    }
    const statuses = statusesKey.split('|') as KPIRequestStatus[];
    return list.filter((request) => statuses.includes(request.status as KPIRequestStatus));
  })
);

export const filterKPIRequestsByStatusesJ = (statuses: KPIRequestStatus[]) =>
  filterKPIRequestsByStatusesFamily(statuses.join('|'));

/**
 * Single request by id (atomFamily).
 *
 * Sentinel guard: returns null for ids `<= 0` so consumers that pass
 * `?? -1` or a default-id don't trigger a fetch with a bogus id.
 */
export const kpisRequestByIdStateJ = atomFamily((requestId: number) =>
  atomWithDefault<IKPIRequestDataModel | null | Promise<IKPIRequestDataModel | null>>(async () => {
    if (requestId <= 0) {
      return null;
    }
    try {
      return await fetchKPIRequestById(requestId);
    } catch (error) {
      console.error(error);
      return null;
    }
  })
);

/**
 * Response for a request id. Depends on {@link kpisRequestByIdStateJ} and
 * only fetches when the request status is in the allowed-statuses set.
 *
 * Preserves the original `requestId === -1` early return.
 *
 * FIXME: this is a v2 atom that supersedes the deprecated
 * `kpiRequestResponseByIdState` once `enableResponseAutosave` flag is
 * fully rolled out.
 */
export const kpisRequestResponseByIdState2J = atomFamily((requestId: number) =>
  atomWithDefault<IKPIRequestResponse | null | Promise<IKPIRequestResponse | null>>(async (get) => {
    if (requestId <= 0) {
      return null;
    }
    const request = await get(kpisRequestByIdStateJ(requestId));
    if (request && ALLOWED_KPI_REQUEST_STATUS.has(request.status as KPIRequestStatus)) {
      try {
        return await fetchKPIRequestResponseById(request.id);
      } catch (error) {
        console.error(error);
        return null;
      }
    }
    return null;
  })
);

/**
 * Map of companyId → requests for that company, derived from
 * {@link kpisRequestsListStateJ}. Null-safe — coalesces `null` to `[]` so the
 * map is empty if the fetch resolved to null.
 */
export const kpiRequestsByCompanyStateJ = atom(async (get) => {
  const list = (await get(kpisRequestsListStateJ)) ?? [];
  const map = new Map<number, IKPIRequestDataModel[]>();
  list.forEach((request) => {
    if (map.has(request.companyId)) {
      map.get(request.companyId)!.push(request);
    } else {
      map.set(request.companyId, [request]);
    }
  });
  return map;
});
